import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { getCharactersForLanguage } from '../utils/alphabets';
import './TheCircle.css';

// Couleur par défaut si aucune n'est choisie
const DEFAULT_COLOR = '#FFFFFF';

// Taille par défaut de l'export (carré côté x côté en pixels)
const DEFAULT_EXPORT_SIZE = 800;

// Luminosité relative (0-1) selon la formule W3C
const getLuminance = (hex) => {
  if (!hex || !hex.startsWith('#')) return 1;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Texte blanc si fond < 40% luminosité, sinon noir
const getTextColor = (bgColor) => (getLuminance(bgColor || DEFAULT_COLOR) < 0.4 ? '#FFFFFF' : '#000000');

// Mapping saisons -> mois (pour dériver les couleurs des mois)
// Printemps: 3,4,5 | Été: 6,7,8 | Automne: 9,10,11 | Hiver: 12,1,2
const SEASON_TO_MONTH = { 0: '3', 1: '6', 2: '9', 3: '12' };

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const angleRad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
};

const describeAnnulusSector = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const p1 = polarToCartesian(cx, cy, rInner, startAngle);
  const p2 = polarToCartesian(cx, cy, rOuter, startAngle);
  const p3 = polarToCartesian(cx, cy, rOuter, endAngle);
  const p4 = polarToCartesian(cx, cy, rInner, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${p3.x} ${p3.y} L ${p4.x} ${p4.y} A ${rInner} ${rInner} 0 ${largeArc} 0 ${p1.x} ${p1.y} Z`;
};

// Arc path pour textPath - le texte suit le périmètre du cercle
// reverse=true pour les secteurs en bas (texte à l'endroit)
const describeArcPath = (cx, cy, r, startAngle, endAngle, reverse = false) => {
  const p1 = polarToCartesian(cx, cy, r, reverse ? endAngle : startAngle);
  const p2 = polarToCartesian(cx, cy, r, reverse ? startAngle : endAngle);
  const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  const sweep = reverse ? 0 : 1;
  return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${p2.x} ${p2.y}`;
};

const TheCircle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [matrices, setMatrices] = useState([]);
  const [selectedMatrice, setSelectedMatrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const circleRef = useRef(null);

  useEffect(() => {
    loadMatrices();
  }, []);

  const loadMatrices = async () => {
    try {
      const response = await api.get('/matrices/');
      const matricesList = response.data.results || response.data;
      setMatrices(matricesList);
      if (matricesList.length > 0 && !selectedMatrice) {
        setSelectedMatrice(matricesList[0]);
      }
    } catch (error) {
      console.error('Error loading matrices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMatriceChange = (e) => {
    const matriceId = parseInt(e.target.value);
    const matrice = matrices.find(m => m.id === matriceId);
    setSelectedMatrice(matrice);
  };

  const handleCreateMatrice = () => {
    navigate('/matrice');
  };

  const renderCircle = () => {
    if (!selectedMatrice) return null;

    const langue = selectedMatrice.langue || 'fr';
    const characters = getCharactersForLanguage(langue);
    const letters = characters.letters;
    const chiffres = characters.numbers || '0123456789'.split('');
    const days = characters.days || [];
    const months = characters.months || [];
    const seasons = characters.seasons || ['Spring', 'Summer', 'Autumn', 'Winter'];

    const lettresCouleurs = selectedMatrice.lettres_couleurs || {};
    const chiffresCouleurs = selectedMatrice.chiffres_couleurs || {};
    const moisCouleurs = selectedMatrice.mois_annee_couleurs || {};
    const joursCouleurs = selectedMatrice.jours_semaine_couleurs || {};
    // Fallback sur l'email si le prénom n'est pas renseigné
    const prenomRaw = selectedMatrice.utilisateur_prenom || selectedMatrice.utilisateur_email?.split('@')[0] || '';
    // Format prénom pour affichage : 1, 2 ou 3 lignes selon règles (max 10 car/ligne)
    const formatPrenomLines = (raw) => {
      const upper = raw.toUpperCase().trim();
      if (!upper) return ['Prénom'];
      const parts = upper.split(/[\s-]+/).filter(Boolean);
      if (parts.length >= 3) {
        return parts.slice(0, 3).map((p) => p.slice(0, 10));
      }
      if (parts.length === 2) {
        return [parts[0].slice(0, 10), parts[1].slice(0, 10)];
      }
      if (upper.length > 10) {
        const truncated = upper.slice(0, 20);
        return [truncated.slice(0, 10), truncated.slice(10)];
      }
      return [upper];
    };
    const prenomLines = formatPrenomLines(prenomRaw);

    const getColor = (color) => color || DEFAULT_COLOR;

    const cx = 250;
    const cy = 250;
    const size = 500;

    // Rayons des cercles (de l'extérieur vers l'intérieur)
    const r1 = 240; // Cercle 1 (saisons) - extérieur
    const r2 = 200; // Cercle 2 (mois)
    const r3 = 160; // Cercle 3 (jours)
    const r4 = 130; // Cercle 4 (lettres)
    const r5 = 95;  // Cercle 5 (prénom) - partie supérieure
    const r6 = 45;  // Cercle 5 - partie inférieure / Cercle 6 blanc

    const elements = [];
    const defs = [];

    // --- Cercle 1 : Saisons (4 secteurs) - texte suit le périmètre ---
    const nSeasons = 4;
    const anglePerSeason = 360 / nSeasons;
    const textR1 = (r1 + r2) / 2;
    for (let i = 0; i < nSeasons; i++) {
      const startAngle = i * anglePerSeason;
      const endAngle = (i + 1) * anglePerSeason;
      const midAngle = (startAngle + endAngle) / 2;
      const reverse = midAngle > 90 && midAngle < 270;
      const pathId = `arc-season-${i}`;
      defs.push(<path key={pathId} id={pathId} d={describeArcPath(cx, cy, textR1, startAngle, endAngle, reverse)} />);
      const monthKey = SEASON_TO_MONTH[i];
      const color = getColor(moisCouleurs[monthKey]);
      const d = describeAnnulusSector(cx, cy, r1, r2, startAngle, endAngle);
      elements.push(
        <g key={`season-${i}`}>
          <path d={d} fill={color} stroke="#333" strokeWidth="0.5" className="circle-sector" />
          <text className="circle-text circle-text-season" style={{ fill: getTextColor(color) }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">{seasons[i]}</textPath>
          </text>
        </g>
      );
    }

    // --- Cercle 2 : Mois (12 secteurs) - texte suit le périmètre ---
    // Rotation pour aligner Juin, Juillet, Août dans le quadrant Été (90-180°)
    // Janvier à 300°, Mars à 0° (Printemps), Juin à 90° (Été), Sept à 180° (Automne), Déc à 270° (Hiver)
    const nMonths = 12;
    const anglePerMonth = 360 / nMonths;
    const monthOffset = 300; // Janvier commence à 300° pour que l'été (Juin-Juillet-Août) soit à 90-180°
    const textR2 = (r2 + r3) / 2;
    for (let i = 0; i < nMonths; i++) {
      let startAngle = (monthOffset + i * anglePerMonth) % 360;
      let endAngle = (monthOffset + (i + 1) * anglePerMonth) % 360;
      if (endAngle === 0) endAngle = 360; // Février : 330-0 → 330-360 pour éviter le bug d'arc
      const midAngle = (startAngle + endAngle) / 2;
      const reverse = midAngle > 90 && midAngle < 270;
      const pathId = `arc-month-${i}`;
      defs.push(<path key={pathId} id={pathId} d={describeArcPath(cx, cy, textR2, startAngle, endAngle, reverse)} />);
      const monthKey = String(i + 1);
      const color = getColor(moisCouleurs[monthKey]);
      const d = describeAnnulusSector(cx, cy, r2, r3, startAngle, endAngle);
      const monthName = months[i] || '';
      elements.push(
        <g key={`month-${i}`}>
          <path d={d} fill={color} stroke="#333" strokeWidth="0.5" className="circle-sector" />
          <text className="circle-text circle-text-month" style={{ fill: getTextColor(color) }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">{monthName}</textPath>
          </text>
        </g>
      );
    }

    // --- Cercle 3 : Jours (7 secteurs) - texte suit le périmètre ---
    const nDays = 7;
    const anglePerDay = 360 / nDays;
    const textR3 = (r3 + r4) / 2;
    for (let i = 0; i < nDays; i++) {
      const startAngle = i * anglePerDay;
      const endAngle = (i + 1) * anglePerDay;
      const midAngle = (startAngle + endAngle) / 2;
      const reverse = midAngle > 90 && midAngle < 270;
      const pathId = `arc-day-${i}`;
      defs.push(<path key={pathId} id={pathId} d={describeArcPath(cx, cy, textR3, startAngle, endAngle, reverse)} />);
      const dayKey = String(i);
      const color = getColor(joursCouleurs[dayKey]);
      const d = describeAnnulusSector(cx, cy, r3, r4, startAngle, endAngle);
      const dayName = days[i] || '';
      elements.push(
        <g key={`day-${i}`}>
          <path d={d} fill={color} stroke="#333" strokeWidth="0.5" className="circle-sector" />
          <text className="circle-text circle-text-day" style={{ fill: getTextColor(color) }}>
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">{dayName}</textPath>
          </text>
        </g>
      );
    }

    // --- Cercle 4 : Lettres (26 ou plus selon la langue) ---
    const nLetters = letters.length;
    const anglePerLetter = 360 / nLetters;
    for (let i = 0; i < nLetters; i++) {
      const startAngle = i * anglePerLetter;
      const endAngle = (i + 1) * anglePerLetter;
      const letter = letters[i];
      const color = getColor(lettresCouleurs[letter]);
      const d = describeAnnulusSector(cx, cy, r4, r5, startAngle, endAngle);
      const midAngle = (startAngle + endAngle) / 2;
      const textR = (r4 + r5) / 2;
      const textPos = polarToCartesian(cx, cy, textR, midAngle);
      elements.push(
        <g key={`letter-${i}`}>
          <path d={d} fill={color} stroke="#333" strokeWidth="0.5" className="circle-sector" />
          <text x={textPos.x} y={textPos.y} textAnchor="middle" dominantBaseline="middle" className="circle-text circle-text-letter" style={{ fill: getTextColor(color) }}>{letter}</text>
        </g>
      );
    }

    // --- Cercle 5 : Partie supérieure (chiffres), Partie inférieure (prénom complet) ---
    const r5mid = (r5 + r6) / 2; // Frontière entre partie sup et inf du cercle 5

    // Partie supérieure du cercle 5 : chiffres (0-9) en secteurs radiaux
    const nChiffres = chiffres.length;
    const anglePerChiffre = 360 / nChiffres;
    for (let i = 0; i < nChiffres; i++) {
      const startAngle = i * anglePerChiffre;
      const endAngle = (i + 1) * anglePerChiffre;
      const chiffre = chiffres[i];
      const color = getColor(chiffresCouleurs[chiffre]);
      const d = describeAnnulusSector(cx, cy, r5, r5mid, startAngle, endAngle);
      const midAngle = (startAngle + endAngle) / 2;
      const textR = (r5 + r5mid) / 2;
      const textPos = polarToCartesian(cx, cy, textR, midAngle);
      elements.push(
        <g key={`chiffre-${i}`}>
          <path d={d} fill={color} stroke="#333" strokeWidth="0.5" className="circle-sector" />
          <text x={textPos.x} y={textPos.y} textAnchor="middle" dominantBaseline="middle" className="circle-text circle-text-chiffre" style={{ fill: getTextColor(color) }}>{chiffre}</text>
        </g>
      );
    }

    // Partie inférieure du cercle 5 : prénom complet (une bande continue) - sans stroke pour éviter le trait noir
    const prenomColor = DEFAULT_COLOR;
    const dPrenom = describeAnnulusSector(cx, cy, r5mid, r6, 0, 360);
    elements.push(
      <g key="prenom-full">
        <path d={dPrenom} fill={prenomColor} className="circle-sector" />
      </g>
    );

    // --- Cercle 6 : Cercle blanc au centre - sans stroke ---
    const dWhite = describeAnnulusSector(cx, cy, r6, 0, 0, 360);
    elements.push(
      <g key="center-white">
        <path d={dWhite} fill={DEFAULT_COLOR} className="circle-sector" />
      </g>
    );

    // Texte au centre (prénom ou placeholder) - chaque lettre avec sa couleur de l'alphabet
    const centerBgColor = DEFAULT_COLOR;
    const nLines = prenomLines.length;
    const getLineDy = (idx) => {
      if (nLines === 1) return '0';
      if (nLines === 2) return idx === 0 ? '-0.5em' : '1em';
      if (nLines === 3) return idx === 0 ? '-1em' : '1em';
      return idx === 0 ? `${-(nLines - 1) / 2}em` : '1em';
    };
    elements.push(
      <text key="center-text" x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" className="circle-text circle-text-prenom-full">
        {prenomLines.map((line, lineIdx) => (
          <tspan key={lineIdx} x={cx} dy={getLineDy(lineIdx)}>
            {line.split('').map((letter, i) => {
              const letterColor = lettresCouleurs[letter];
              const fillColor = letterColor || getTextColor(centerBgColor);
              return <tspan key={i} style={{ fill: fillColor }}>{letter}</tspan>;
            })}
          </tspan>
        ))}
      </text>
    );

    return (
      <div className="circle-container" ref={circleRef}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="color-circle">
          <defs>{defs}</defs>
          {elements}
        </svg>
      </div>
    );
  };

  const handleShare = async () => {
    if (!circleRef.current) return;
    try {
      setLoading(true);
      const isDark = document.body.classList.contains('dark-mode');
      const bgColor = isDark ? '#0f0f0f' : '#f5f5f5';
      const sourceCanvas = await html2canvas(circleRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: bgColor,
        logging: false,
      });
      // Export en carré : diamètre du cercle = 90% du côté, sans marges (scale cover)
      const side = DEFAULT_EXPORT_SIZE;
      const targetCircleDiameter = side * 0.9;
      const circleDiameterRatio = 480 / 500;
      const sourceCircleDiameter = Math.min(sourceCanvas.width, sourceCanvas.height) * circleDiameterRatio;
      let scale = targetCircleDiameter / sourceCircleDiameter;
      let scaledW = sourceCanvas.width * scale;
      let scaledH = sourceCanvas.height * scale;
      // Scale pour remplir tout le carré (éviter marges blanches en haut/bas)
      if (scaledW < side || scaledH < side) {
        const coverScale = Math.max(side / sourceCanvas.width, side / sourceCanvas.height);
        scale = Math.max(scale, coverScale * 1.002);
        scaledW = sourceCanvas.width * scale;
        scaledH = sourceCanvas.height * scale;
      }
      const x = (side - scaledW) / 2;
      const y = (side - scaledH) / 2;
      const squareCanvas = document.createElement('canvas');
      squareCanvas.width = side;
      squareCanvas.height = side;
      const ctx = squareCanvas.getContext('2d');
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, side, side);
      ctx.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, x, y, scaledW, scaledH);
      // Lien www.synestesia.world en bas à droite, couleur complémentaire du fond
      const linkColor = isDark ? '#c4c4c4' : '#4a4a4a';
      ctx.font = '18px "Source Sans 3", "source-sans-3", sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = linkColor;
      ctx.fillText('www.synestesia.world', side - 16, side - 16);
      const imgData = squareCanvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `cercle-couleurs-${selectedMatrice?.id || 'matrice'}.png`;
      link.href = imgData;
      link.click();
      setLoading(false);
    } catch (error) {
      console.error(t('thecircle.exportError'), error);
      setLoading(false);
    }
  };

  if (loading && matrices.length === 0) {
    return (
      <div className="thecircle">
        <Navbar />
        <div className="thecircle-content">
          <div className="loading">{t('thecircle.loading')}</div>
        </div>
      </div>
    );
  }

  if (matrices.length === 0) {
    return (
      <div className="thecircle">
        <Navbar />
        <div className="thecircle-content">
          <div className="no-matrix-message">
            <h2>{t('thecircle.noMatrix')}</h2>
            <button onClick={handleCreateMatrice} className="create-matrix-button">
              {t('thecircle.createMatrix')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="thecircle">
      <Navbar />
      <div className="thecircle-content">
        <div className="thecircle-header">
          <h1>{t('thecircle.title')}</h1>
          {matrices.length > 1 && (
            <div className="matrice-selector-container">
              <label htmlFor="matrice-select">{t('thecircle.selectMatrix')}</label>
              <select
                id="matrice-select"
                value={selectedMatrice?.id || ''}
                onChange={handleMatriceChange}
                className="matrice-selector"
              >
                {matrices.map((m) => (
                  <option key={m.id} value={m.id}>
                    Matrice #{m.id} - {m.langue?.toUpperCase() || 'FR'} ({new Date(m.date_creation).toLocaleDateString()})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="circle-wrapper">
          {renderCircle()}
        </div>

        <div className="circle-actions">
          <button
            onClick={handleShare}
            className="gradient-button"
            disabled={loading}
          >
            {t('thecircle.share')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TheCircle;
